import { Autocomplete } from "Components/Autocomplete/Autocomplete";
import { AutocompleteEntry } from "Components/Autocomplete/AutocompleteEntry";
import i18n from "i18next";
import { IChipUtil } from "Interfaces/Utils/IChipUtil";
import { resolve } from "inversify-react";
import { TaskTemplateTag } from "Models/Tasks/TaskTemplateTag";
import * as React from "react";
import Types from "ServiceProvider/Types";
import { ITaskTemplateState } from "Store/TaskTemplates/TaskTemplateState";
import { TaskTemplateTagSearch } from "./TaskTemplateTagSearch/TaskTemplateTagSearch";

interface IProps {
    taskTemplateState: ITaskTemplateState;
    selectedTaskTemplateTags?: TaskTemplateTag[];
    className?: string;
    onSelectionChanged(selectedTaskTemplateTags?: TaskTemplateTag[]): void;
}

interface IState {
    taskTemplateTags?: TaskTemplateTag[];
    selectedTaskTemplateTags?: TaskTemplateTag[];
    filter: string;
    showAutocomplete: boolean;
    autocompleteEntries?: AutocompleteEntry[];
    hasHighlight: boolean;
}

export class TaskTemplateTagSelection extends React.Component<IProps, IState> {
    @resolve(Types.IChipUtil) public chipUtil: IChipUtil;

    constructor(props: IProps) {
        super(props);

        this.state = {
            showAutocomplete: false,
            taskTemplateTags: props.taskTemplateState.taskTemplateTags ? props.taskTemplateState.taskTemplateTags : [],
            autocompleteEntries: undefined,
            filter: "",
            selectedTaskTemplateTags: props.selectedTaskTemplateTags,
            hasHighlight: false
        };

        this.onTaskTemplateTagSelectedHandler = this.onTaskTemplateTagSelectedHandler.bind(this);
        this.onTaskTemplateTagFilterChangedHandler = this.onTaskTemplateTagFilterChangedHandler.bind(this);
        this.onFocusHandler = this.onFocusHandler.bind(this);
        this.onBlurHandler = this.onBlurHandler.bind(this);
        this.onAutocompleteEntrySelectedHandler = this.onAutocompleteEntrySelectedHandler.bind(this);
        this.addNewTag = this.addNewTag.bind(this);
        this.insertTagIntoSelectedTags = this.insertTagIntoSelectedTags.bind(this);
        this.onHighlightChanged = this.onHighlightChanged.bind(this);
    }

    public static getDerivedStateFromProps(nextProps: IProps, prevState: IState): Partial<IState | null> {
        if (nextProps.taskTemplateState.taskTemplateTags !== prevState.taskTemplateTags) {
            return {
                taskTemplateTags: nextProps.taskTemplateState.taskTemplateTags ? nextProps.taskTemplateState.taskTemplateTags : [],
            };
        }
        return null;
    }

    public onFocusHandler() {
        this.setState({ showAutocomplete: true });
    }

    public onBlurHandler() {
        this.setState({ showAutocomplete: false });
    }

    public onAutocompleteEntrySelectedHandler(entry: AutocompleteEntry) {
        this.setState({
            showAutocomplete: false 
        });

        this.insertTagIntoSelectedTags(entry.item);
    }

    public onTaskTemplateTagSelectedHandler(selectedTaskTemplateTags: TaskTemplateTag[]) {
        this.setState({
            selectedTaskTemplateTags,
            filter: "",
            showAutocomplete: false,
            autocompleteEntries: undefined
        });

        if (this.props.onSelectionChanged) {
            this.props.onSelectionChanged(selectedTaskTemplateTags);
        }
    }

    public addNewTag(value: string) {
        if (this.state.hasHighlight) {
            return;
        }
        
        const taskTemplateTags = this.state.taskTemplateTags ? this.state.taskTemplateTags : [];
        const existingTag = taskTemplateTags.find(x => x.name === value);

        const newTag: TaskTemplateTag = {
            name: value,
            companyId: 0
        };

        if (!existingTag) {
            taskTemplateTags.push(newTag);

            this.setState({
                taskTemplateTags
            });
        }

        this.insertTagIntoSelectedTags(existingTag ? existingTag : newTag);
    }
    

    public insertTagIntoSelectedTags(selectedTag: TaskTemplateTag) {
        const newSelectedTaskTemplateTags = this.state.selectedTaskTemplateTags ? this.state.selectedTaskTemplateTags : [];
        newSelectedTaskTemplateTags.push(selectedTag);

        this.onTaskTemplateTagSelectedHandler(newSelectedTaskTemplateTags);
    }

    public onHighlightChanged(hasHighlight: boolean) {
        this.setState({
            hasHighlight
        });
    }

    public onTaskTemplateTagFilterChangedHandler(filter: string) {
        if (!filter) {
            this.setState({ filter, showAutocomplete: false, autocompleteEntries: undefined });
        }
        else {
            const result: TaskTemplateTag[] = [];
            filter = filter.toLowerCase();
    
            if(this.props.taskTemplateState.taskTemplateTags) {
                this.props.taskTemplateState.taskTemplateTags.forEach(taskTemplateTag => {
                    const taskTemplateTagName = taskTemplateTag.name.toLowerCase();
        
                    if (taskTemplateTagName.indexOf(filter) >= 0 && this.state.selectedTaskTemplateTags && this.state.selectedTaskTemplateTags.filter(x => x.name === taskTemplateTag.name).length === 0) {
                        result.push(taskTemplateTag);
                    }
                });
            }

            this.setState({
                filter,
                showAutocomplete: true,
                autocompleteEntries: result.map(item => {
                    return { key: item.id, text: item.name, item };
                })
            });
        }
    }

    public render() {
        return (
            <Autocomplete
                show={this.state.showAutocomplete}
                entries={this.state.autocompleteEntries}
                onBlur={this.onBlurHandler}
                onFocus={this.onFocusHandler}
                onEntrySelected={this.onAutocompleteEntrySelectedHandler}
                noItemsFoundMessage={i18n.t("tasks.tags.noTagsFound")}
                onHighlightChanged={this.onHighlightChanged}>
                <TaskTemplateTagSearch
                    tags={this.state.selectedTaskTemplateTags}
                    placeholder={i18n.t("audits.placeholders.auditItemTagSearch")}
                    onFilterTaskTemplateTags={this.onTaskTemplateTagFilterChangedHandler}
                    onUpdateTags={this.onTaskTemplateTagSelectedHandler}
                    onSubmitCurrentValue={this.addNewTag}
                    filter={this.state.filter}
                />
            </Autocomplete>
        );
    }
}


// WEBPACK FOOTER //
// ./src/Components/TaskTemplateTagSelection/TaskTemplateTagSelection.tsx